import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, BriefcaseBusiness } from "lucide-react";

/**
 * Gestione mansioni per tenant (solo super admin).
 * Predefinite di sistema: sola lettura. Custom: crea / cambia attributo
 * timbra (propagato subito agli utenti) / elimina (solo se non in uso).
 */
export const JobTitlesManager = ({ orgId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [clocksIn, setClocksIn] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/job-titles", { params: { org_id: orgId } })
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Impossibile caricare le mansioni"))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post("/superadmin/job-titles", { org_id: orgId, name: name.trim(), clocks_in: clocksIn });
      toast.success("Mansione creata");
      setName("");
      setClocksIn(true);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore creazione");
    } finally { setBusy(false); }
  };

  const toggleClocks = async (jt) => {
    try {
      await api.patch(`/superadmin/job-titles/${jt.id}`, { clocks_in: !jt.clocks_in });
      toast.success(`«${jt.name}»: timbratura ${!jt.clocks_in ? "attivata" : "disattivata"} (applicata subito agli utenti)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore aggiornamento");
    }
  };

  const remove = async (jt) => {
    try {
      await api.delete(`/superadmin/job-titles/${jt.id}`);
      toast.success("Mansione eliminata");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore eliminazione");
    }
  };

  if (loading) return <div className="text-sm text-text-muted py-4">Caricamento mansioni…</div>;

  return (
    <div className="space-y-5" data-testid="job-titles-manager">
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Mansione</th>
              <th className="text-left px-5 py-3 font-medium">Timbra</th>
              <th className="text-left px-5 py-3 font-medium">Origine</th>
              <th className="text-right px-5 py-3 font-medium">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map((jt) => (
              <tr key={jt.id} className="border-t border-border" data-testid={`job-title-row-${jt.id}`}>
                <td className="px-5 py-3 text-text-primary flex items-center gap-2">
                  <BriefcaseBusiness size={13} className="text-text-muted" /> {jt.name}
                </td>
                <td className="px-5 py-3">
                  {jt.is_default ? (
                    jt.clocks_in
                      ? <span className="text-xs px-2 py-0.5 rounded-md bg-success-subtle text-success-strong">Sì</span>
                      : <span className="text-xs px-2 py-0.5 rounded-md bg-subtle text-text-muted">No</span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Switch
                        checked={!!jt.clocks_in}
                        onCheckedChange={() => toggleClocks(jt)}
                        data-testid={`job-title-clocks-${jt.id}`}
                      />
                      <span className="text-xs text-text-secondary">{jt.clocks_in ? "Sì" : "No"}</span>
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 text-xs text-text-secondary">
                  {jt.is_default ? "Predefinita" : "Custom tenant"}
                </td>
                <td className="px-5 py-3 text-right">
                  {!jt.is_default && (
                    <button
                      type="button"
                      title="Elimina (solo se non in uso)"
                      onClick={() => remove(jt)}
                      data-testid={`job-title-delete-${jt.id}`}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-md text-danger hover:bg-danger-subtle"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form onSubmit={add} className="bg-surface border border-border rounded-xl p-5 flex items-end gap-3 flex-wrap" data-testid="job-title-add-form">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs font-medium text-text-secondary">Nuova mansione custom</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            placeholder="es. Tecnico installatore"
            className="mt-1.5 h-10 rounded-md border-border bg-surface"
            data-testid="job-title-name-input"
          />
        </div>
        <div className="flex items-center gap-2 pb-2.5">
          <Switch checked={clocksIn} onCheckedChange={setClocksIn} data-testid="job-title-clocks-input" />
          <span className="text-sm text-text-secondary">Timbra</span>
        </div>
        <Button
          type="submit"
          disabled={busy || name.trim().length < 2}
          data-testid="job-title-add-btn"
          className="bg-accent hover:bg-accent-strong text-white rounded-md h-10 px-4"
        >
          <Plus size={15} className="mr-1.5" /> Aggiungi
        </Button>
      </form>
      <p className="text-xs text-text-muted">
        L'attributo «Timbra» determina automaticamente widget timbratura, conteggi presenze e «Chi c'è oggi».
        Cambiarlo su una mansione custom si applica subito a tutti gli utenti che la ricoprono.
      </p>
    </div>
  );
};
